import type { ComponentType } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/utils'
import {
  GithubLogoIcon,
  MicrosoftOutlookLogoIcon,
  OpenAiLogoIcon,
} from '@phosphor-icons/react'

/**
 * Product marks for the integrations and AI-app surfaces, so a row reads as
 * the product it connects to. Phosphor ships GitHub, Slack-style, Outlook and
 * OpenAI marks; the AI-app marks (Claude, Cursor, VS Code, Grok, Meta,
 * OpenClaw) are the products' own vector marks, via Simple Icons and LobeHub
 * Icons, in each product's own color.
 * Every logo takes only `className` so it can stand in for a nav icon.
 */
export type BrandLogo = ComponentType<{ className?: string }>

type LogoProps = { className?: string }

const box = (className?: string) => cn('h-4 w-4 shrink-0', className)

export function ChatGptLogo({ className }: LogoProps) {
  return <OpenAiLogoIcon className={box(className)} weight="fill" />
}

/** Claude's spark, in Anthropic's terracotta. */
export function ClaudeLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="#D97757"
        d="M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z"
      />
    </svg>
  )
}

/** Cursor's cube mark; monochrome like Cursor's own. */
export function CursorLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M22.106 5.68L12.5.135a.998.998 0 00-.998 0L1.893 5.68a.84.84 0 00-.419.726v11.186c0 .3.16.577.42.727l9.607 5.547a.999.999 0 00.998 0l9.608-5.547a.84.84 0 00.42-.727V6.407a.84.84 0 00-.42-.726zm-.603 1.176L12.228 22.92c-.063.108-.228.064-.228-.061V12.34a.59.59 0 00-.295-.51l-9.11-5.26c-.107-.062-.063-.228.062-.228h18.55c.264 0 .428.286.296.514z"
      />
    </svg>
  )
}

/** VS Code's ribbon, in its blue. */
export function VsCodeLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="#007ACC"
        d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"
      />
    </svg>
  )
}

/** OpenClaw's lobster, in its own red (the project's mark). */
export function OpenClawLogo({ className }: LogoProps) {
  // Gradient ids are per instance: a page can show the mark several times,
  // and a shared id would resolve to the first copy, even a hidden one.
  const id = useId().replace(/:/g, '')
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <defs>
        <linearGradient
          id={`${id}-body`}
          gradientUnits="userSpaceOnUse"
          x1="-.659"
          x2="27.023"
          y1=".458"
          y2="22.855"
        >
          <stop stopColor="#FF4D4D" />
          <stop offset="1" stopColor="#991B1B" />
        </linearGradient>
        <linearGradient
          id={`${id}-left`}
          gradientUnits="userSpaceOnUse"
          x1="0"
          x2="4.311"
          y1="9.672"
          y2="14.949"
        >
          <stop stopColor="#FF4D4D" />
          <stop offset="1" stopColor="#991B1B" />
        </linearGradient>
        <linearGradient
          id={`${id}-right`}
          gradientUnits="userSpaceOnUse"
          x1="19.385"
          x2="24.399"
          y1="9.953"
          y2="14.462"
        >
          <stop stopColor="#FF4D4D" />
          <stop offset="1" stopColor="#991B1B" />
        </linearGradient>
      </defs>
      <path
        d="M12 2.568c-6.33 0-9.495 5.275-9.495 9.495 0 4.22 3.165 8.44 6.33 9.494v2.11h2.11v-2.11s1.055.422 2.11 0v2.11h2.11v-2.11c3.165-1.055 6.33-5.274 6.33-9.494S18.33 2.568 12 2.568z"
        fill={`url(#${id}-body)`}
      />
      <path
        d="M3.56 9.953C.396 8.898-.66 11.008.396 13.118c1.055 2.11 3.164 1.055 4.22-1.055.632-1.477 0-2.11-1.056-2.11z"
        fill={`url(#${id}-left)`}
      />
      <path
        d="M20.44 9.953c3.164-1.055 4.22 1.055 3.164 3.165-1.055 2.11-3.164 1.055-4.22-1.055-.632-1.477 0-2.11 1.056-2.11z"
        fill={`url(#${id}-right)`}
      />
      <path
        d="M5.507 1.875c.476-.285 1.036-.233 1.615.037.577.27 1.223.774 1.937 1.488a.316.316 0 01-.447.447c-.693-.693-1.279-1.138-1.757-1.361-.475-.222-.795-.205-1.022-.069a.317.317 0 01-.326-.542zM16.877 1.913c.58-.27 1.14-.323 1.616-.038a.317.317 0 01-.326.542c-.227-.136-.547-.153-1.022.069-.478.223-1.064.668-1.756 1.361a.316.316 0 11-.448-.447c.714-.714 1.36-1.218 1.936-1.487z"
        fill="#FF4D4D"
      />
      <path
        d="M8.835 9.109a1.266 1.266 0 100-2.532 1.266 1.266 0 000 2.532zM15.165 9.109a1.266 1.266 0 100-2.532 1.266 1.266 0 000 2.532z"
        fill="#050810"
      />
      <path
        d="M9.046 8.16a.527.527 0 100-1.056.527.527 0 000 1.055zM15.376 8.16a.527.527 0 100-1.055.527.527 0 000 1.054z"
        fill="#00E5CC"
      />
    </svg>
  )
}

/** Meta's infinity mark, in Meta blue (Muse is Meta's assistant). */
export function MetaLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="#0081FB"
        d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z"
      />
    </svg>
  )
}

/** Grok's mark; monochrome like xAI's own, so it follows the text color. */
export function GrokLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M9.27 15.29l7.978-5.897c.391-.29.95-.177 1.137.272.98 2.369.542 5.215-1.41 7.169-1.951 1.954-4.667 2.382-7.149 1.406l-2.711 1.257c3.889 2.661 8.611 2.003 11.562-.953 2.341-2.344 3.066-5.539 2.388-8.42l.006.007c-.983-4.232.242-5.924 2.75-9.383.06-.082.12-.164.179-.248l-3.301 3.305v-.01L9.267 15.292M7.623 16.723c-2.792-2.67-2.31-6.801.071-9.184 1.761-1.763 4.647-2.483 7.166-1.425l2.705-1.25a7.808 7.808 0 00-1.829-1A8.975 8.975 0 005.984 5.83c-2.533 2.536-3.33 6.436-1.962 9.764 1.022 2.487-.653 4.246-2.34 6.022-.599.63-1.199 1.259-1.682 1.925l7.62-6.815"
      />
    </svg>
  )
}

/** Gmail's M. */
export function GmailLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path fill="#EA4335" d="M4 6.5 12 12.5l8-6V5H4z" />
      <path fill="#4285F4" d="M4 7.8V19h3.5v-8.6z" />
      <path fill="#34A853" d="M20 7.8V19h-3.5v-8.6z" />
      <path fill="#FBBC04" d="M7.5 10.4 12 13.8l4.5-3.4V19h-9z" />
    </svg>
  )
}

export function GoogleCalendarLogo({ className }: LogoProps) {
  return (
    <img
      alt=""
      aria-hidden
      className={cn(box(className), 'object-contain')}
      src="/integrations/google_calendar.png"
    />
  )
}

export function OutlookLogo({ className }: LogoProps) {
  return (
    <MicrosoftOutlookLogoIcon
      className={box(className)}
      weight="fill"
      color="#0078D4"
    />
  )
}

export function OutlookCalendarLogo({ className }: LogoProps) {
  return (
    <img
      alt=""
      aria-hidden
      className={cn(box(className), 'object-contain')}
      src="/integrations/ms365_calendar.png"
    />
  )
}

/**
 * Slack's four-color hash as vectors. The old slack_icon.jpg was a padded
 * 16:9 photo on white: tiny in a square box and a white patch in dark mode.
 */
export function SlackLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 127 127" aria-hidden className={box(className)}>
      <path
        fill="#E01E5A"
        d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
      />
      <path
        fill="#36C5F0"
        d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z"
      />
      <path
        fill="#2EB67D"
        d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
      />
      <path
        fill="#ECB22E"
        d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
      />
    </svg>
  )
}

export function GitHubLogo({ className }: LogoProps) {
  return <GithubLogoIcon className={box(className)} weight="fill" />
}

/** Zoom's camera tile. */
export function ZoomLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="#2D8CFF" />
      <path
        fill="#fff"
        d="M6.5 8.5h7a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1zm9.5 2.2 2.5-1.7v6l-2.5-1.7z"
      />
    </svg>
  )
}

export function ScramblCaptureLogo({ className }: LogoProps) {
  return (
    <img
      alt=""
      aria-hidden
      className={cn(box(className), 'object-contain')}
      src="/icon.svg"
    />
  )
}

/** Google's G, in its four colors. */
export function GoogleLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <path
        fill="#4285F4"
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"
      />
      <path
        fill="#FBBC04"
        d="M6.4 14a6 6 0 0 1 0-3.9V7.4H3.1a10 10 0 0 0 0 9.1z"
      />
      <path
        fill="#EA4335"
        d="M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.4L6.4 10c.8-2.3 3-4 5.6-4z"
      />
    </svg>
  )
}

/** Microsoft's four squares. */
export function MicrosoftLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={box(className)}>
      <rect x="3" y="3" width="8.5" height="8.5" fill="#F25022" />
      <rect x="12.5" y="3" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="3" y="12.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  )
}
